/**
 * tutorLiveChatAdapter — les fils du chat du live, vus du masteur : « Tous » puis un fil par élève
 * rattaché à la salle, présent ou non (décision 5 de la salle du tuteur).
 *
 * Port littéral de l'ancien chat du tuteur (`common/liveMode/LiveChat.js:65-121`) :
 *  - les membres du chat sont les élèves du roster (`liveSession.users`, sans les tuteurs), pas les
 *    présents de l'appel ;
 *  - « Tous » (l'ancien id 0) ouvre le fil de la séance (`thread_id`) ;
 *  - le fil d'un élève est `thread_ids[élève]`, à défaut le fil du groupe (`thread_ids[id] ?? thread_id`) ;
 *  - les non-lus : `unread_private_messages_count[élève]` pour un élève, `unread_messages_count` pour
 *    « Tous » (`ChatAvatar`). La collection PHP vide sort en `[]` : un tableau se lit comme un objet vide.
 *
 * Ce que le crochet du chat lit d'ici : le fil d'un canal, à qui l'annoncer, et les puces.
 */
import { type UILiveChatChannelOption } from "../../../ui/types/studentLive"

/** « Tous », le fil de la séance : un id qui ne croise jamais celui d'un élève (des nombres). */
export const TUTOR_GROUP_CHANNEL = "group"

export type ApiTutorLiveMember = {
    id?: number | string | null
    is_tutor?: boolean | null
    display_name?: string | null
    firstname?: string | null
    lastname?: string | null
    avatar?: string | null
    /** Les champs que la prise de rendez-vous d'une séance d'essai lit (`openCalendly`). */
    parent_firstname?: string | null
    parent_lastname?: string | null
    parent_email?: string | null
    parent_mobile?: string | null
    email?: string | null
}

export type ApiTutorLiveSession = {
    id?: number | string | null
    thread_id?: number | string | null
    thread_ids?: Record<string, number | string | null | undefined> | unknown[] | null
    users?: (ApiTutorLiveMember | null)[] | null
    /** Un objet `{id d'élève: n}`, ou `[]` quand la collection est vide (le sérialiseur PHP). */
    unread_private_messages_count?: Record<string, number | null | undefined> | unknown[] | null
    unread_messages_count?: number | null
    live_room?: { type?: string | null; name?: string | null } | null
}

/** Une puce : son canal, son fil, l'élève à qui il parle (`null` : « Tous »), ses non-lus. */
export type TutorChatChannelEntry = {
    channel: string
    threadId: string | null
    studentId: string | null
    unread: number
}

/** Un objet indexé par id, ou rien : le tableau vide de PHP en est un. */
function keyed<T>(value: Record<string, T> | unknown[] | null | undefined): Record<string, T> {
    if (value == null || Array.isArray(value)) return {}
    return value
}

const count = (value: unknown): number => (typeof value === "number" && Number.isFinite(value) ? value : 0)

/** Les élèves rattachés à la salle, dans l'ordre du roster (`users`, sans les tuteurs). */
export function tutorLiveStudentsOf(session: ApiTutorLiveSession | null | undefined): ApiTutorLiveMember[] {
    return (session?.users ?? []).filter(
        (member): member is ApiTutorLiveMember => member != null && member.id != null && member.is_tutor !== true
    )
}

/** Les puces, dans leur ordre : « Tous », puis chaque élève du roster. */
export function tutorChatChannels(session: ApiTutorLiveSession | null | undefined): TutorChatChannelEntry[] {
    if (session == null) return []
    const group = session.thread_id != null ? String(session.thread_id) : null
    const threads = keyed(session.thread_ids as Record<string, number | string | null | undefined> | null)
    const privateUnread = keyed(session.unread_private_messages_count as Record<string, number | null> | null)
    const entries: TutorChatChannelEntry[] = [
        {
            channel: TUTOR_GROUP_CHANNEL,
            threadId: group,
            studentId: null,
            unread: count(session.unread_messages_count),
        },
    ]
    for (const student of tutorLiveStudentsOf(session)) {
        const id = String(student.id)
        const thread = threads[id]
        entries.push({
            channel: id,
            threadId: thread != null ? String(thread) : group,
            studentId: id,
            unread: count(privateUnread[id]),
        })
    }
    return entries
}

/** Le fil d'un canal ; un canal inconnu (un élève parti du roster) : `null`. */
export function tutorChatThreadOf(session: ApiTutorLiveSession | null | undefined, channel: string): string | null {
    return tutorChatChannels(session).find((entry) => entry.channel === channel)?.threadId ?? null
}

/**
 * À qui un message du canal s'annonce : l'élève du fil, ou toute la salle dans « Tous » — comme
 * `CommonChat` (`currentUser?.id === 0 ? null : [currentUser?.id]`).
 */
export function tutorChatRecipients(channel: string): number[] | null {
    if (channel === TUTOR_GROUP_CHANNEL) return null
    const id = Number(channel)
    return Number.isFinite(id) ? [id] : null
}

/** Le canal d'un fil (un message reçu dit son fil, pas son canal) : le premier qui l'ouvre. */
export function tutorChatChannelOfThread(
    session: ApiTutorLiveSession | null | undefined,
    threadId: string | null | undefined
): string | null {
    if (threadId == null) return null
    const entries = tutorChatChannels(session)
    // Le fil du groupe est « Tous », même quand un élève sans fil privé l'ouvre aussi.
    const group = entries.find((entry) => entry.channel === TUTOR_GROUP_CHANNEL)
    if (group?.threadId === threadId) return TUTOR_GROUP_CHANNEL
    return entries.find((entry) => entry.threadId === threadId)?.channel ?? null
}

/** Le prénom d'un membre : le sien, sinon le premier mot de son nom affiché. */
export function memberFirstname(member: ApiTutorLiveMember | null | undefined): string {
    const firstname = member?.firstname?.trim()
    if (firstname != null && firstname !== "") return firstname
    return member?.display_name?.trim().split(/\s+/)[0] ?? ""
}

/** Les puces du `<LiveChat viewer="tutor">` : « Tous », puis chaque élève, son prénom et son visage. */
export function tutorChatChannelOptions(
    session: ApiTutorLiveSession | null | undefined,
    avatarOf: (path: string | null | undefined) => string | undefined
): UILiveChatChannelOption[] {
    const students = new Map(tutorLiveStudentsOf(session).map((member) => [String(member.id), member]))
    return tutorChatChannels(session).map((entry) => {
        const member = entry.studentId != null ? students.get(entry.studentId) : undefined
        if (member == null) return { id: entry.channel, unread: entry.unread }
        const avatarUrl = avatarOf(member.avatar)
        return {
            id: entry.channel,
            unread: entry.unread,
            student: { firstname: memberFirstname(member), ...(avatarUrl != null ? { avatarUrl } : {}) },
        }
    })
}
